import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect') || '/';

  // If already logged in, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirectTo);
    } catch (error) {
      
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="login-page">
      <div className="max-w-md mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-10">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Welcome Back</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white">Login</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6"
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6 pr-12"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          <Button 
            type="submit"
            disabled={loading}
            className="w-full btn-hover bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-6 text-sm uppercase tracking-widest font-semibold"
            data-testid="login-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner mr-2" />
                Signing in...
              </span>
            ) : (
              <>
                Sign In
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-[#999999] mt-8">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="register-link">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
